import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import * as Types from 'types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { selectors } from '../reducer'
import * as apiActions from 'api-actions'
import { BackButton, FullPageSpinner } from 'components'
import { Redirect, useHistory, useParams } from 'react-router-dom'
import { displayEin } from 'utils'
import {
  AgreementDetailsActions,
  AgreementDetailsModals,
  AgreementDetailsTabContent,
} from '../components'
import { Path, AroActionItems } from 'config/portal'
import {
  ServicesAgreementStatus,
  CombinedServiceAgreementStatus,
} from 'config/automatic-rollovers'
import { TOAST_ERROR_CONTACT_SUPPORT } from 'config/notifications'
import { flashErrorMessage } from 'redux-flash'
import * as actions from '../actions'
import { downloadServicesAgreementFile } from 'api'
import { TabBar } from 'lp-components'
import ServicesAgreementParticipants from './ServicesAgreementParticipants'
import { selectors as globalSelectors } from '../../reducer'
import { ProductAgreementType } from 'config/products'
import participantFieldMappings from '../components/participantFieldMapping'

const propTypes = {
  fetchServicesAgreementDetails: PropTypes.func.isRequired,
  servicesAgreement: Types.servicesAgreementShow,
  flashErrorMessageHandler: PropTypes.func.isRequired,
  clearServicesAgreementDetails: PropTypes.func.isRequired,
  fetchAvailableInitialInvestments: PropTypes.func.isRequired,
  availableInitialInvestments: PropTypes.arrayOf(
    Types.availableInitialInvestment
  ),
  shouldTriggerServicesAgreementDetailsFetch: PropTypes.bool.isRequired,
  setShouldTriggerServicesAgreementDetailsFetch: PropTypes.func.isRequired,
  setShouldTriggerAllServicesAgreementsFetch: PropTypes.func.isRequired,
  setShouldTriggerAllBDPlanAgreementsFetch: PropTypes.func.isRequired,
  currentUser: Types.user.isRequired,
}

const defaultProps = {
  servicesAgreement: null,
  availableInitialInvestments: null,
}

const PARTICIPANTS_INITIAL_FILTERS = {
  status: [],
}

function NoDetailsProvidedCard({ sectionName }) {
  return (
    <div className="card no-details">
      <h2 className="section-header">{sectionName} Details</h2>
      <p className="not-provided">Not provided</p>
    </div>
  )
}
NoDetailsProvidedCard.propTypes = {
  sectionName: PropTypes.string.isRequired,
}

const Tab = {
  AGREEMENT: 'agreement',
  PARTICIPANTS: 'participants',
}

function PlanAgreementDetails({
  fetchServicesAgreementDetails,
  servicesAgreement,
  flashErrorMessageHandler,
  clearServicesAgreementDetails,
  fetchAvailableInitialInvestments,
  availableInitialInvestments,
  shouldTriggerServicesAgreementDetailsFetch,
  setShouldTriggerServicesAgreementDetailsFetch,
  setShouldTriggerAllServicesAgreementsFetch,
  setShouldTriggerAllBDPlanAgreementsFetch,
  currentUser,
}) {
  const history = useHistory()
  const [errorExists, setErrorExists] = useState(false)
  const [showDownloadNotAvailableModal, setShowDownloadNotAvailableModal] =
    useState(false)
  const [showResendModal, setShowResendModal] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const { servicesAgreementId } = useParams()
  const [currentTab, setCurrentTab] = useState(Tab.AGREEMENT)
  const [participantsFilters, setParticipantsFilters] = useState(
    PARTICIPANTS_INITIAL_FILTERS
  )
  const [participantsSearch, setParticipantsSearch] = useState('')

  useEffect(() => {
    return () => {
      clearServicesAgreementDetails()
      setShouldTriggerServicesAgreementDetailsFetch(true)
    }
  }, [])

  useEffect(() => {
    async function getInitialInvestments() {
      try {
        await fetchAvailableInitialInvestments()
      } catch {
        setErrorExists(true)
      }
    }
    if (!availableInitialInvestments) getInitialInvestments()
  }, [availableInitialInvestments])

  useEffect(() => {
    async function getPlanAgreementDetails() {
      try {
        await fetchServicesAgreementDetails(servicesAgreementId)
        setShouldTriggerServicesAgreementDetailsFetch(false)
      } catch {
        setErrorExists(true)
      }
    }
    if (shouldTriggerServicesAgreementDetailsFetch) getPlanAgreementDetails()
  }, [shouldTriggerServicesAgreementDetailsFetch])

  const isLoading =
    (!servicesAgreement || !availableInitialInvestments) && !errorExists

  if (isLoading) return <FullPageSpinner />
  if (errorExists) {
    flashErrorMessageHandler(TOAST_ERROR_CONTACT_SUPPORT)
    return <Redirect to={Path.SERVICES_AGREEMENTS} />
  }

  const {
    name,
    portalAgreementStatus,
    createdDate,
    planEIN,
    planNumber,
    portalAgreementType,
    planType,
    planStatus,
    providerPlanId,
    planSponsorCompany,
    planSponsorContact,
    consultantCompany,
    consultantContact,
    TPACompany,
    TPAContact,
    recordKeeperCompany,
    recordKeeperContact,
    signingMethod,
  } = servicesAgreement

  const handleDownloadSelect = async () => {
    try {
      await downloadServicesAgreementFile(servicesAgreementId, name)
    } catch (e) {
      if (e.status === 422) setShowDownloadNotAvailableModal(true)
      else setErrorExists(true)
    }
  }

  const displayPlanEinValue = displayEin(planEIN)

  const agreementAudience = currentUser.accountAudience

  const aroActionItems = [
    {
      externalLink: process.env.REACT_APP_ARO_FUNDING_INSTRUCTIONS_URL,
      text: AroActionItems.VIEW_FUNDING_INSTRUCTIONS,
    },
    {
      handleSelect: () => handleDownloadSelect(),
      text: 'Download completed agreement',
    },
  ]

  return (
    <div className="services-agreement-details-view">
      <div className="header-bar">
        <BackButton
          link={(location) => {
            return {
              pathname: Path.SERVICES_AGREEMENTS,
              search: location.state?.search,
            }
          }}
        >
          Back to all agreements
        </BackButton>
        <div className="header-bar-content">
          <div className="header-bar-page-title">
            <h1 className="page-title">{name}</h1>
          </div>
          <AgreementDetailsActions
            portalAgreementStatus={portalAgreementStatus}
            signingMethod={signingMethod}
            actionItems={aroActionItems}
            actionMenuLabel="View automatic rollover action items"
            onInitiate={() =>
              history.push('/automatic-rollovers/select-rollover-type')
            }
            onUploadSignedAgreement={() => setShowUploadModal(true)}
            onResendDocusignEmail={() => setShowResendModal(true)}
            onDownloadAgreement={handleDownloadSelect}
            initiateLabel={AroActionItems.INITIATE_ROLLOVER}
            uploadSignedAgreementLabel={AroActionItems.UPLOAD_SIGNED_AGREEMENT}
            resendDocusignEmailLabel={AroActionItems.RESEND_DOCUSIGN_EMAIL}
            downloadAgreementLabel={AroActionItems.DOWNLOAD_AGREEMENT}
          />
        </div>
      </div>
      {(portalAgreementStatus === ServicesAgreementStatus.COMPLETED ||
        portalAgreementStatus ===
          CombinedServiceAgreementStatus.COMBINED_COMPLETED) && (
        <TabBar
          options={[
            { key: 'Agreement Details', value: Tab.AGREEMENT },
            { key: 'Participants', value: Tab.PARTICIPANTS },
          ]}
          value={currentTab}
          onChange={setCurrentTab}
        />
      )}
      {currentTab === Tab.AGREEMENT && (
        <AgreementDetailsTabContent
          isLoading={shouldTriggerServicesAgreementDetailsFetch}
          name={name}
          portalAgreementStatus={portalAgreementStatus}
          createdDate={createdDate}
          planNumber={planNumber}
          providerPlanId={providerPlanId}
          portalAgreementType={portalAgreementType}
          planType={planType}
          planStatus={planStatus}
          agreementAudience={agreementAudience}
          planSponsorCompany={planSponsorCompany}
          planSponsorContact={planSponsorContact}
          displayPlanEinValue={displayPlanEinValue}
          TPACompany={TPACompany}
          TPAContact={TPAContact}
          consultantCompany={consultantCompany}
          consultantContact={consultantContact}
          recordKeeperCompany={recordKeeperCompany}
          recordKeeperContact={recordKeeperContact}
          NoDetailsProvidedCard={NoDetailsProvidedCard}
        />
      )}
      {currentTab === Tab.PARTICIPANTS && (
        <ServicesAgreementParticipants
          servicesAgreementId={servicesAgreementId}
          baseFilters={PARTICIPANTS_INITIAL_FILTERS}
          filters={participantsFilters}
          handleFilter={(filters) => setParticipantsFilters(filters)}
          search={participantsSearch}
          handleSearch={(q) => setParticipantsSearch(q)}
          fieldMappings={participantFieldMappings}
        />
      )}
      <AgreementDetailsModals
        showUploadModal={showUploadModal}
        setShowUploadModal={setShowUploadModal}
        showDownloadNotAvailableModal={showDownloadNotAvailableModal}
        setShowDownloadNotAvailableModal={setShowDownloadNotAvailableModal}
        showResendModal={showResendModal}
        setShowResendModal={setShowResendModal}
        servicesAgreement={servicesAgreement}
        servicesAgreementId={servicesAgreementId}
        portalAgreementStatus={portalAgreementStatus}
        agreementsType={ProductAgreementType.ARO}
        onUploadSuccess={() => {
          setShowUploadModal(false)
          setShouldTriggerServicesAgreementDetailsFetch(true)
          setShouldTriggerAllServicesAgreementsFetch(true)
          setShouldTriggerAllBDPlanAgreementsFetch(true)
        }}
      />
    </div>
  )
}

function mapStateToProps(state) {
  return {
    servicesAgreement: selectors.servicesAgreement(state),
    availableInitialInvestments: selectors.availableInitialInvestments(state),
    shouldTriggerServicesAgreementDetailsFetch:
      selectors.shouldTriggerServicesAgreementDetailsFetch(state),
    currentUser: globalSelectors.currentUser(state),
  }
}

const mapDispatchToProps = {
  fetchServicesAgreementDetails: apiActions.fetchServicesAgreementDetails,
  flashErrorMessageHandler: flashErrorMessage,
  clearServicesAgreementDetails: actions.clearServicesAgreementDetails,
  fetchAvailableInitialInvestments: apiActions.fetchAvailableInitialInvestments,
  setShouldTriggerServicesAgreementDetailsFetch:
    actions.setShouldTriggerServicesAgreementDetailsFetch,
  setShouldTriggerAllServicesAgreementsFetch:
    actions.setShouldTriggerAllServicesAgreementsFetch,
  setShouldTriggerAllBDPlanAgreementsFetch:
    actions.setShouldTriggerAllBDPlanAgreementsFetch,
}

PlanAgreementDetails.propTypes = propTypes
PlanAgreementDetails.defaultProps = defaultProps

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  PlanAgreementDetails
)
